// @ts-check
import imageCompression from 'browser-image-compression';
import { Edit2, Upload, X } from 'lucide-react';
import { useState } from 'react';
import api from '../utils/apiClient';
import { FocalPointButton, focalToObjectPosition } from './ui/FocalPointPicker';
import ImagePreviewButton from './ui/ImagePreviewButton';

/**
 * Single-image upload field with preview.
 *
 * Optional focal point support: pass `focal` ({x, y} | null) and `onFocalChange` to show a
 * crosshair button over the preview that opens the FocalPointPicker. The focal point is
 * held in the parent's form state (not persisted here) and is reset to null whenever a
 * new image is uploaded or the image is cleared. `focalPreviews` lists the aspect ratios
 * the storefront crops this image to.
 */
const ImageUploadPreview = ({
  value,
  onChange,
  label = 'Image',
  uploadType = 'product-image',
  className = '',
  focal = null,
  onFocalChange = null,
  focalPreviews = undefined,
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);

      // Compress if > 10MB
      let fileToUpload = file;
      if (file.size > 10 * 1024 * 1024) {
        const options = {
          maxSizeMB: 10,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressed = await imageCompression(file, options);
        fileToUpload = new File([compressed], file.name, { type: compressed.type });
      }

      const formData = new FormData();
      formData.append('file', fileToUpload);

      const data = await api.upload(`/api/upload/${uploadType}`, formData);
      setPreview(data.url);
      onChange(data.url);
      onFocalChange?.(null); // a new picture invalidates the old focal point
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setPreview('');
    onChange('');
    onFocalChange?.(null);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-2">{label}</label>

      {preview ? (
        <div className="relative group inline-block">
          <ImagePreviewButton src={preview} alt={label} />
          {onFocalChange && (
            <FocalPointButton src={preview} value={focal} previews={focalPreviews} onSave={(f) => onFocalChange(f)} />
          )}
          <img
            src={preview}
            alt={label}
            className="w-full max-w-md h-48 object-cover rounded-lg border border-input"
            style={{ objectPosition: focalToObjectPosition(focal) }}
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <label className="cursor-pointer p-2 bg-card rounded-full shadow hover:bg-accent">
              <Edit2 size={18} />
              <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" disabled={uploading} />
            </label>
            <button type="button" onClick={handleClear} className="p-2 bg-card rounded-full shadow hover:bg-accent">
              <X size={18} />
            </button>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center w-full max-w-md h-48 border-2 border-dashed border-input rounded-lg cursor-pointer hover:border-muted-foreground bg-muted/50">
          {uploading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </div>
          ) : (
            <>
              <Upload size={32} className="text-muted-foreground/70 mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload image</p>
              <p className="text-xs text-muted-foreground/70 mt-1">PNG, JPG up to 10MB</p>
            </>
          )}
          <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" disabled={uploading} />
        </label>
      )}
    </div>
  );
};

export default ImageUploadPreview;
